import { DatePipe } from '@angular/common';
import { PhonePipe } from './shared/pipes/phone';
import { ElementRef } from '@angular/core';
import { ICoordinates } from './shared/components/tip/tip.component';
import { Action } from '@ngrx/store';
import { PostalCodePatternCombined } from './common/abstract.components/constants';
import { environment } from '../environment/environment';

const datePipe = new DatePipe('en-US');
const phonePipe = new PhonePipe();
const userAgent = navigator.userAgent /* istanbul ignore next */ || navigator.vendor;

export function getIsSafari() {
  // Chrome has Safari in the user agent so we need to filter
  const isChrome = userAgent.indexOf('Chrome') > -1;
  const isSafari = userAgent.indexOf('Safari') > -1;

  return isChrome && isSafari ? false : isSafari;
}

// eslint-disable-next-line no-useless-escape
export const isMobileDevice = /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(userAgent) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(userAgent.substr(0, 4));

const typeCache: { [label: string]: boolean } = {};

export function type<T>(label: T | ''): T {
  if (typeCache[<string> label]) {
    throw new Error(`Action type "${ label}" is not unique"`);
  }

  typeCache[<string> label] = true;

  return <T> label;
}

export function ofType(type: string) {
  return (action: Action): boolean => action.type === type;
}

export const toPayload = (action): any => action.payload;

export function getPropFrom(
  source: any,
  path: string | string[],
  defaultValue: any = '',
  allowedValues: any[] = null,
  allowedTypes: string[] = ['number', 'string', 'boolean']) {
  if (typeof path === 'string') {
    path = path.split('.');
  }

  const prop = path.shift();

  // eslint-disable-next-line no-prototype-builtins
  if (source && source.hasOwnProperty(prop)) {
    if (path.length === 0) {
      const isAllowedValue = !allowedValues || allowedValues && allowedValues.indexOf(source[prop]) !== -1;
      const isValidType = !allowedTypes || allowedTypes && allowedTypes.indexOf(typeof source[prop]) !== -1;

      return isValidType && isAllowedValue ? source[prop] : defaultValue;
    } else {
      return getPropFrom(source[prop], path, defaultValue, allowedValues, allowedTypes);
    }
  } else {
    return defaultValue;
  }
}

export function getFormattedDateOfBirth(date, format): string {
  return date ? datePipe.transform(date.split('T')[0], format) : '';
}

export function getFormatedPhoneNumber(phoneNumber): string {
  return phoneNumber ? phonePipe.transform(phoneNumber) : '';
}

export function getFormatedSsn(ssn) {
  return ssn ? ssn.replace(/-/g, '') : '';
}

export function parseUrlHash(hash): any {
  const result = {};

  hash.split('&').forEach((part) => {
    const [key, value] = part.split('=');

    result[key] = value;
  });

  return result;
}

export function requestFullScreen(element) {
  if (element.requestFullscreen) {
    element.requestFullscreen();
  } else if (element.mozRequestFullScreen) {
    element.mozRequestFullScreen();
  } else if (element.webkitRequestFullScreen) {
    element.webkitRequestFullScreen();
  } else if (element.msRequestFullscreen) {
    element.msRequestFullscreen();
  }
}

const exitFullscreenMethod = (() => {
  /* istanbul ignore next */
  if (document['exitFullscreen']) {
    return 'exitFullscreen';
  } else if (document['webkitExitFullscreen']) {
    return 'webkitExitFullscreen';
  } /* istanbul ignore next */ else if (document['mozCancelFullScreen']) {
    return 'mozCancelFullScreen';
  } /* istanbul ignore next */ else if (document['msExitFullscreen']) {
    return 'msExitFullscreen';
  } else {
    return null;
  }
})();

export function exitFullScreen() {
  document[exitFullscreenMethod]();
}

export function isFullscreenEnabled() {
  return window.innerHeight === screen.height;
}

export function enumHasValue(intrf, value) {
  return Object.keys(intrf).some((prop) => intrf[prop] === value);
}

export function prepareErrorMessage(message: any): string {
  return typeof message === 'string'
    ? message.split(/(?:\r\n|\r|\n)/).filter((s) => s.trim()).join('. ')
    : '';
}

export function sendGoogleAnalyticsPageChange(url) {
  /* istanbul ignore next */
  
  if (environment.production) {
    (<any> window).ga('set', 'page', url);
    (<any> window).ga('send', 'pageview');
  }
}

export enum eventCategory {
  click = 'Click'
}

interface IGoogleEvent {
  eventCategory: eventCategory;
  eventLabel: string;
  eventAction: string;
  eventValue: number;
}

export function sendGoogleAnalyticsEvent(googleEvent: IGoogleEvent) {
  /* istanbul ignore next */
  if (environment.production) {
    (<any> window).ga('send', 'event', googleEvent);
  }
}

export function getRawBase64Data(dataString) {
  return dataString.split(',')[1];
}

const bodyTagBegin = '<body';
// eslint-disable-next-line no-useless-escape
const bodyTagEnd = '<\/body>';

function extractBody(html: string, bodyBeginIndex: number): string {
  const bodyBeginCloseIndex: number = bodyBeginIndex + html.substr(bodyBeginIndex).indexOf('>') + 1;
  const bodyEndIndex: number = html.indexOf(bodyTagEnd);

  return html.substring(bodyBeginCloseIndex, bodyEndIndex);
}

export function extractHtmlBodyContent(rawHtml: string): string {
  const bodyBeginIndex = rawHtml.indexOf(bodyTagBegin);

  return bodyBeginIndex > -1
    ? extractBody(rawHtml, bodyBeginIndex)
    : rawHtml;
}

export function getElementCenterCoordinates(elementRef: ElementRef): ICoordinates {
  const rect = elementRef.nativeElement.getBoundingClientRect();
  const halfWidth = rect.width / 2;
  const halfHeight = rect.height / 2;

  return {
    top: rect.top + window.pageYOffset + halfHeight,
    left: rect.left + window.pageXOffset + halfWidth
  };
}

function combineErrorsMessages(errors): string {
  return Object.keys(errors).map((key) => {
    return errors[key].join('. ');
  }).join('. ');
}

export function getErrorResponseMessage(err): string {
  const { message, errors } = err;

  return message ? message : combineErrorsMessages(errors);
}

export function removeSpacesFromString(str: string): string {
  return str
    ? str.replace(/\s/gm, '')
    : str;
}

export function formatPostalCode(str: string): string {
  return str
    ? PostalCodePatternCombined.test(str)
      ? str
      : str.slice(0, 3).concat(' ').concat(str.slice(3))
    : str;
}
